'use client';

import type { SettingsProviderProps } from '../types';

import { useAuthContext } from 'src/auth/hooks';

import { SettingsProvider } from './settings-provider';
import { SETTINGS_STORAGE_KEY } from '../settings-config';

// ----------------------------------------------------------------------

/**
 * Dark mode / contrast / nav-side preferences were stored under one
 * device-wide cookie key, so switching accounts on the same browser kept
 * showing the previous account's theme — logging out and back in as
 * someone else never changed anything.
 *
 * Wraps SettingsProvider and remounts it (via `key`) under a per-user
 * storage key once the authenticated user is known, so each account gets
 * its own saved preferences and logging out returns to the anonymous
 * default bucket.
 */
export function UserScopedSettingsProvider({ children, ...other }: SettingsProviderProps) {
  const { user } = useAuthContext();
  const storageKey = user?.id ? `${SETTINGS_STORAGE_KEY}:${user.id}` : SETTINGS_STORAGE_KEY;

  return (
    <SettingsProvider key={storageKey} storageKey={storageKey} {...other}>
      {children}
    </SettingsProvider>
  );
}
